import { ApiResponse, axiosInstance } from '@/lib/axios';
import { FetchParams, IPagination } from '@/types/general.type';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TChannel } from '../../../_shared/types/channel.type';

export type TGetThreadsChannelsResponse = ApiResponse<IPagination<TChannel>>;
export type TGetThreadsChannelsParams = FetchParams;

export const getThreadsChannels = async (params: TGetThreadsChannelsParams): Promise<TGetThreadsChannelsResponse> => {
  const url = '/integration/threads/org-channel';

  return axiosInstance.get(url, { params });
};

export const GET_THREADS_CHANNELS_QUERY_KEY = 'get-threads-channels';

export const getThreadsChannelsQueryOptions = <TData = IPagination<TChannel>>(
  params: TGetThreadsChannelsParams,
  select?: (data: TGetThreadsChannelsResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_THREADS_CHANNELS_QUERY_KEY,
      {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        sortDir: params?.sortDir,
      },
    ],
    queryFn: () => getThreadsChannels(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useThreadsChannelsQuery<TData>(options: {
  params: TGetThreadsChannelsParams;
  queryConfig?: QueryConfig<typeof getThreadsChannelsQueryOptions<TData>>;
  select: (data: TGetThreadsChannelsResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useThreadsChannelsQuery(options: {
  params: TGetThreadsChannelsParams;
  queryConfig?: QueryConfig<typeof getThreadsChannelsQueryOptions<TGetThreadsChannelsResponse>>;
}): ReturnType<typeof useQuery<TGetThreadsChannelsResponse>>;

// Implementation
export function useThreadsChannelsQuery<TData = TGetThreadsChannelsResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetThreadsChannelsParams;
  queryConfig?: QueryConfig<typeof getThreadsChannelsQueryOptions<TData | TGetThreadsChannelsResponse>>;
  select?: (data: TGetThreadsChannelsResponse) => TData;
}) {
  return useQuery({
    ...getThreadsChannelsQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetThreadsChannelsResponse ? TGetThreadsChannelsResponse : TData>>;
}
